import React from "react";
import { Box, Typography } from "@mui/material";
import ScheduleIcon from "@mui/icons-material/Schedule";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { unwrapDescriptionString } from "../../utils/descriptionUtils";
import { resolveKnowledgeBaseContent, parseKnowledgeBaseTables } from "../../utils/knowledgeBaseFormat";
import { formatShortWeekdayDateTime } from "../../../../utils/dateUtils";

export default function DriveKnowledgeBaseEntityBody({
  entityBodyText,
  meta,
  tc,
  htmlProseSx,
  isKnowledgeBase,
  useKbFormattedLayout,
}) {

  /* ─── Table styles ───────────────────────────────────────────── */
  const kbTableSx = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "0.8rem",
    tableLayout: "auto",
    "& thead": {
      position: "sticky",
      top: 0,
      zIndex: 2,
    },
    "& thead th": {
      backgroundColor: "#F8FAFF",
      color: tc.text.secondary,
      fontWeight: 600,
      fontSize: "0.7rem",
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      px: 2,
      py: 1.25,
      textAlign: "left",
      whiteSpace: "nowrap",
      boxShadow: "0 1px 0 rgba(59,130,246,0.1)",
    },
    "& tbody td": {
      px: 2,
      py: 1.25,
      color: tc.text.primary,
      borderBottom: `1px solid ${tc.border.subtle}`,
      verticalAlign: "top",
      lineHeight: 1.65,
      fontSize: "0.8rem",
    },
    "& tbody tr:last-child td": { borderBottom: "none" },
    "& tbody tr:hover td": { backgroundColor: "rgba(59,130,246,0.018)" },
  };

  /** Same blue family as `blue` / control-id links (#2563EB); softer fill, no border — table row count only */
  const Chip = ({ children, variant = "default" }) => {
    const styles = {
      blue: { bgcolor: "rgba(59,130,246,0.08)", color: "#2563EB", border: "1px solid rgba(59,130,246,0.15)" },
      blueSoft: { bgcolor: "rgba(59,130,246,0.1)", color: "#2563EB", border: "none" },
      mono: { bgcolor: tc.background?.tableRow ?? "rgba(0,0,0,0.04)", color: tc.text.secondary, border: `1px solid ${tc.border.subtle}`, fontFamily: "monospace" },
      default: { bgcolor: tc.background?.tableRow ?? "rgba(0,0,0,0.04)", color: tc.text.secondary, border: `1px solid ${tc.border.subtle}` },
    };
    return (
      <Box sx={{
        display: "inline-flex", alignItems: "center",
        px: 0.9, py: 0.18,
        borderRadius: "4px",
        fontSize: "0.62rem",
        fontWeight: 600,
        letterSpacing: "0.02em",
        lineHeight: 1.5,
        ...styles[variant],
      }}>
        {children}
      </Box>
    );
  };

  /* ─── Table renderer ─────────────────────────────────────────── */
  const renderKbTables = (bodyRaw) => {
    const parsed = parseKnowledgeBaseTables(bodyRaw);
    if (!parsed || parsed.tables.length === 0) return null;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {parsed.remainingText && (
          <Typography sx={{ fontSize: "0.855rem", lineHeight: 1.7, color: tc.text.primary, whiteSpace: "pre-wrap" }}>
            {parsed.remainingText}
          </Typography>
        )}
        {parsed.tables.map((table, tIdx) => {
          const isControlMapping = table.headers[0]?.toLowerCase().includes("criteria");
          const isEvidenceMatrix = table.headers.some((h) => h.toLowerCase().includes("evidence"));
          const tableLabel = isControlMapping ? "Control Mapping" : isEvidenceMatrix ? "Evidence Matrix" : `Table ${tIdx + 1}`;

          return (
            <Box key={tIdx}>
              {/* Table label — clean, no left bar */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                <Typography sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: tc.text.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.07em",
                }}>
                  {tableLabel}
                </Typography>
                <Chip variant="blueSoft">{table.rows.length} rows</Chip>
              </Box>

              {/* Table */}
              <Box sx={{
                borderRadius: "8px",
                border: `1px solid ${tc.border.subtle}`,
                overflow: "hidden",
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              }}>
                <Box sx={{ overflow: "auto", maxHeight: 380 }}>
                  <Box component="table" sx={kbTableSx}>
                    <thead>
                      <tr>
                        {table.headers.map((h, hi) => <th key={hi}>{h}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {table.rows.map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => {
                            const isControlId =
                              table.headers[ci]?.toLowerCase().includes("control id") ||
                              table.headers[ci]?.toLowerCase().includes("criteria id");
                            return (
                              <td key={ci}>
                                {isControlId ? (
                                  <Box component="span" sx={{
                                    fontWeight: 600,
                                    color: "#2563EB",
                                    fontFamily: "monospace",
                                    fontSize: "0.77rem",
                                    letterSpacing: "0.02em",
                                  }}>
                                    {cell}
                                  </Box>
                                ) : cell}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  /* ─── KB Header — no left bar, no clutter ───────────────────── */
  const KbHeader = ({ eyebrow, title, badges }) => {
    const filteredBadges = badges?.filter(Boolean) ?? [];
    return (
      <Box sx={{
        px: 2.5,
        pt: 1.75,
        pb: 1.5,
        borderBottom: `1px solid ${tc.border.subtle}`,
        backgroundColor: "rgba(248,250,255,0.7)",
      }}>
        {/* Eyebrow + inline badges (no title) OR just eyebrow */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.65, flexWrap: "wrap" }}>
          <MenuBookIcon sx={{ fontSize: 13, color: "#3B82F6", opacity: 0.8 }} />
          <Typography sx={{
            fontSize: "0.65rem",
            color: "#3B82F6",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            fontWeight: 700,
          }}>
            {eyebrow}
          </Typography>
          {/* If no title, show badges inline with eyebrow */}
          {(!title || title === "Unknown Page") && filteredBadges.map((b, i) => (
            <Box key={i}>{b}</Box>
          ))}
        </Box>

        {/* Title */}
        {title && title !== "Unknown Page" && (
          <Typography sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            color: tc.text.strong,
            lineHeight: 1.3,
            letterSpacing: "-0.01em",
            mt: 0.75,
            mb: filteredBadges.length ? 0.85 : 0,
          }}>
            {title}
          </Typography>
        )}

        {/* Badges below title (only when title exists) */}
        {title && title !== "Unknown Page" && filteredBadges.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            {filteredBadges}
          </Box>
        )}
      </Box>
    );
  };

  /* ─── Body wrapper───────────────────────────────────────────── */
  const Body = ({ children }) => (
    <Box sx={{ px: 2.5, py: 2.25 }}>{children}</Box>
  );

  /* ─── Inline badges ──────────────────────────────────────────── */
  const PageTypeBadge = ({ pageType }) =>
    pageType && pageType !== "unknown"
      ? <Chip variant="blue">{pageType}</Chip>
      : null;

  const SequenceBadge = ({ sequence }) =>
    sequence != null && sequence !== ""
      ? <Chip variant="mono">Block {sequence}</Chip>
      : null;

  const DateBadge = ({ createdAt }) =>
    createdAt ? (
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.35 }}>
        <ScheduleIcon sx={{ fontSize: 11, color: tc.text.disabled }} />
        <Typography sx={{ fontSize: "0.65rem", color: tc.text.disabled, letterSpacing: "0.01em" }}>
          {formatShortWeekdayDateTime(createdAt)}
        </Typography>
      </Box>
    ) : null;

  const ProseText = ({ children }) => (
    <Typography sx={{
      fontSize: "0.855rem",
      lineHeight: 1.75,
      color: tc.text.primary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}>
      {children || "—"}
    </Typography>
  );

  /* ═══════════════════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════════════════ */

  if (useKbFormattedLayout) {
    const descStr = unwrapDescriptionString(entityBodyText);
    const kb = resolveKnowledgeBaseContent(descStr);
    if (kb) {
      const bodyRaw = kb.body || "";
      const bodyIsHtml = /<[a-z][\s\S]*>/i.test(bodyRaw.trim());
      const kbTables = renderKbTables(bodyRaw);
      return (
        <Box>
          <KbHeader
            eyebrow={`Knowledge Base${kb.subtitle ? ` · ${kb.subtitle}` : ""}`}
            title={kb.displayTitle}
            badges={[
              <PageTypeBadge key="pt" pageType={meta.pageType} />,
              <SequenceBadge key="seq" sequence={kb.sequence} />,
              <DateBadge key="dt" createdAt={meta.createdAt} />,
            ]}
          />
          <Body>
            {kbTables || (bodyIsHtml
              ? <Box sx={{ ...htmlProseSx, p: 0 }} dangerouslySetInnerHTML={{ __html: bodyRaw }} />
              : <ProseText>{bodyRaw}</ProseText>
            )}
          </Body>
        </Box>
      );
    }
  }

  if (isKnowledgeBase) {
    const descStr = unwrapDescriptionString(entityBodyText);
    const kb = resolveKnowledgeBaseContent(descStr);
    const bodyRaw = kb?.body || descStr;
    const kbTables = renderKbTables(bodyRaw);
    return (
      <Box>
        <KbHeader
          eyebrow="Knowledge Base"
          title={kb?.displayTitle}
          badges={[
            <PageTypeBadge key="pt" pageType={meta.pageType} />,
            <SequenceBadge key="seq" sequence={kb?.sequence} />,
            <DateBadge key="dt" createdAt={meta.createdAt} />,
          ]}
        />
        <Body>
          {kbTables || <ProseText>{bodyRaw}</ProseText>}
        </Body>
      </Box>
    );
  }

  const descStr = unwrapDescriptionString(entityBodyText);
  const descIsHtml = /<[a-z][\s\S]*>/i.test((descStr || "").trim());

  // Determine file icon color based on mime type
  const mimeColor = (() => {
    const m = (meta.mimeType || "").toLowerCase();
    if (m.includes("spreadsheet") || m.includes("csv") || m.includes("excel")) return "#0F9D58";
    if (m.includes("presentation") || m.includes("powerpoint")) return "#F4B400";
    if (m.includes("pdf")) return "#DB4437";
    if (m.includes("image") || m.includes("png") || m.includes("jpg")) return "#9334E6";
    return "#4285F4"; // default doc blue
  })();

  // Friendly mime label
  const mimeLabel = (() => {
    const m = (meta.mimeType || "").toLowerCase();
    if (m.includes("document") || m.includes("docs")) return "Document";
    if (m.includes("spreadsheet") || m.includes("sheets")) return "Spreadsheet";
    if (m.includes("presentation") || m.includes("slides")) return "Presentation";
    if (m.includes("pdf")) return "PDF";
    if (m.includes("image")) return "Image";
    if (m.includes("folder")) return "Folder";
    if (m.includes("text/plain")) return "Text File";
    return meta.mimeType || "File";
  })();

  return (
    <Box>
      {/* Drive document header */}
      <Box sx={{
        px: 2.5,
        pt: 2,
        pb: 1.75,
        borderBottom: `1px solid ${tc.border.subtle}`,
        backgroundColor: "rgba(248,250,255,0.7)",
      }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          {/* File type icon */}
          <Box sx={{
            width: 36,
            height: 36,
            borderRadius: "8px",
            bgcolor: `${mimeColor}14`,
            border: `1px solid ${mimeColor}25`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            mt: 0.25,
          }}>
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: mimeColor }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* File name from contentObj or meta */}
            <Typography sx={{
              fontSize: "0.72rem",
              color: "#3B82F6",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              fontWeight: 700,
              mb: 0.5,
            }}>
              Drive File
            </Typography>

            {/* Meta chips row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap", mt: 0.5 }}>
              {meta.mimeType && (
                <Chip variant="blue">{mimeLabel}</Chip>
              )}
              {meta.mimeType && mimeLabel !== meta.mimeType && (
                <Chip variant="mono">{meta.mimeType}</Chip>
              )}
              {meta.createdAt && <DateBadge createdAt={meta.createdAt} />}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Document body — rendered like a document page */}
      <Box sx={{
        px: 2.5,
        py: 2.25,
        "& h1, & h2, & h3": { color: tc.text.strong },
        "& p": { lineHeight: 1.75 },
      }}>
        {descIsHtml ? (
          <Box sx={{
            ...htmlProseSx,
            p: 0,
            fontSize: "0.855rem",
            lineHeight: 1.75,
            color: tc.text.primary,
            "& h1": { fontSize: "1.1rem", fontWeight: 700, mb: 1, mt: 1.5 },
            "& h2": { fontSize: "1rem", fontWeight: 600, mb: 0.75, mt: 1.25 },
            "& h3": { fontSize: "0.92rem", fontWeight: 600, mb: 0.5, mt: 1 },
            "& ul, & ol": { pl: "1.25rem", my: 0.5 },
            "& li": { mb: 0.25, lineHeight: 1.7 },
            "& table": {
              width: "100%",
              borderCollapse: "collapse",
              my: 1,
              fontSize: "0.8rem",
              "& td, & th": {
                border: `1px solid ${tc.border.subtle}`,
                px: 1.5,
                py: 0.75,
                textAlign: "left",
              },
              "& th": {
                bgcolor: "rgba(59,130,246,0.04)",
                fontWeight: 600,
                color: tc.text.secondary,
              },
            },
          }} dangerouslySetInnerHTML={{ __html: descStr }} />
        ) : (
          <ProseText>{descStr}</ProseText>
        )}
      </Box>
    </Box>
  );
}